/**
 * i18next bootstrap (BUILD_SPEC.md §14).
 *
 * Two locale bundles, both bundled with the app — switching languages
 * does not trigger a network fetch. Default language is Arabic per
 * §14.1; the lang store (Zustand, persisted to localStorage) is the
 * authoritative source after first load.
 *
 * Importing this module has the side effect of initializing i18next.
 * Components consume strings via `useTranslation()`.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import ar from "../locales/ar.json";
import en from "../locales/en.json";

export type Locale = "ar" | "en";

void i18n.use(initReactI18next).init({
  resources: {
    ar: { translation: ar },
    en: { translation: en },
  },
  lng: "ar",
  fallbackLng: "en",
  interpolation: {
    // React already escapes interpolated values, so disabling i18next's
    // own escaping avoids double-escape edge cases without weakening
    // XSS protection.
    escapeValue: false,
  },
});

/**
 * Sync the document direction and lang attributes with the current
 * locale. Called from the lang store every time the user toggles.
 *
 * Updating <html lang> also flips the body font family via the rule in
 * styles/index.css; <html dir> drives every Tailwind RTL plugin variant.
 */
export function applyDocumentLocale(locale: Locale): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = locale === "ar" ? "rtl" : "ltr";
}

export { i18n };
